import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Create, Gesture } from '@mui/icons-material';
import SignaturePad from './SignaturePad';
import {
  formatEmployeeDisplayName,
  renderCursiveSignatureDataUrl,
} from '../utils/trainingSignatureUtils';

/**
 * Training e-sign: draw or editable cursive typed signature.
 */
const TrainingSignForm = ({
  user,
  disabled,
  signatureImage,
  onSignatureImageChange,
  onSignatureTextChange,
  padHeight = 180,
}) => {
  const [mode, setMode] = useState('draw');
  const [cursiveLoading, setCursiveLoading] = useState(false);
  const [typedName, setTypedName] = useState('');
  const displayName = formatEmployeeDisplayName(user);

  useEffect(() => {
    setTypedName(displayName);
  }, [displayName, user?.id]);

  useEffect(() => {
    if (mode !== 'cursive') {
      onSignatureTextChange?.(null);
      return undefined;
    }

    const text = typedName.trim();
    onSignatureTextChange?.(text || null);

    if (!text) {
      onSignatureImageChange?.(null);
      return undefined;
    }

    let cancelled = false;
    setCursiveLoading(true);
    renderCursiveSignatureDataUrl(text)
      .then((dataUrl) => {
        if (!cancelled) onSignatureImageChange?.(dataUrl);
      })
      .finally(() => {
        if (!cancelled) setCursiveLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, typedName, onSignatureImageChange, onSignatureTextChange]);

  const handleModeChange = (_, next) => {
    if (!next) return;
    setMode(next);
    if (next === 'draw') {
      onSignatureImageChange?.(null);
      onSignatureTextChange?.(null);
    } else {
      setTypedName(displayName);
    }
  };

  return (
    <Box>
      {mode === 'draw' ? (
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Signing as: <strong>{displayName || '—'}</strong>
        </Typography>
      ) : (
        <TextField
          fullWidth
          size="small"
          label="Signature text"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          disabled={disabled}
          helperText="Defaults to your account name; edit if needed for your signature."
          sx={{ mb: 1.5 }}
        />
      )}

      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={handleModeChange}
        disabled={disabled || !displayName}
        sx={{ mb: 1.5 }}
      >
        <ToggleButton value="draw">
          <Gesture sx={{ fontSize: 18, mr: 0.5 }} />
          Draw signature
        </ToggleButton>
        <ToggleButton value="cursive">
          <Create sx={{ fontSize: 18, mr: 0.5 }} />
          Typed signature
        </ToggleButton>
      </ToggleButtonGroup>

      {mode === 'cursive' ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            minHeight: padHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          {cursiveLoading ? (
            <Typography variant="body2" color="text.secondary">Preparing signature…</Typography>
          ) : signatureImage ? (
            <Box
              component="img"
              src={signatureImage}
              alt="Cursive signature preview"
              sx={{ maxWidth: '100%', maxHeight: padHeight - 16, objectFit: 'contain' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">Enter signature text above</Typography>
          )}
        </Box>
      ) : (
        <SignaturePad
          onChange={onSignatureImageChange}
          disabled={disabled}
          height={padHeight}
        />
      )}
    </Box>
  );
};

export default TrainingSignForm;
